import React from "react";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import HomeWorkIcon from "@material-ui/icons/HomeWork";
import FavoriteIcon from "@material-ui/icons/Favorite";
import BusinessIcon from "@material-ui/icons/Business";
import DescriptionIcon from "@material-ui/icons/Description";

export { formatCurrency, formatDateOnly } from "../Opportunities/helpers";

/**
 * Situações que a API pode devolver. "cancelada" está aqui só por
 * completude — o backend nunca a atribui hoje (ver portfolioTypes.ts no
 * backend), mas o rótulo/cor já existe caso isso mude no futuro.
 */
export const SITUACAO_CONFIG = {
  a_vencer: { label: "A vencer", color: "#EF6C00" },
  vigente: { label: "Vigente", color: "#5C6BC0" },
  vencida: { label: "Vencida", color: "#C62828" },
  sem_apolice_ativa: { label: "Sem apólice ativa", color: "#757575" },
  cancelada: { label: "Cancelada", color: "#424242" },
};

export const situacaoLabel = (situacao) =>
  (SITUACAO_CONFIG[situacao] || SITUACAO_CONFIG.sem_apolice_ativa).label;

export const situacaoColor = (situacao) =>
  (SITUACAO_CONFIG[situacao] || SITUACAO_CONFIG.sem_apolice_ativa).color;

/** Opções fixas do filtro de Situação — a API nunca produz "cancelada". */
export const SITUACAO_FILTERS = [
  { value: "", label: "Todas" },
  { value: "a_vencer", label: "A vencer" },
  { value: "vigente", label: "Vigente" },
  { value: "vencida", label: "Vencida" },
  { value: "sem_apolice_ativa", label: "Sem apólice ativa" },
];

/** Filtro de Ocorrências: "" = Todas (não envia o parâmetro). Rótulos via i18n carteiraOcorrencias.<chave>. */
export const OCORRENCIAS_FILTERS = [
  { value: "", labelKey: "todas" },
  { value: "sem", labelKey: "sem" },
  { value: "em_aberto", labelKey: "em_aberto" },
  { value: "resolvidas", labelKey: "resolvidas" },
];

/** Situação na visão por mês de vencimento (valores de ?situacao= em /carteira/vencimentos). */
export const VENC_SITUACAO_FILTERS = [
  { value: "", labelKey: "todas" },
  { value: "renovada", labelKey: "renovada" },
  { value: "vencida", labelKey: "vencida" },
  { value: "nao_renovada", labelKey: "naoRenovada" },
  { value: "nova", labelKey: "nova" },
];

/** Valor do query param "ocorrencias" (/carteira e /carteira/vencimentos); undefined = não enviar. */
export function ocorrenciasParam(value) {
  return OCORRENCIAS_FILTERS.some((option) => option.value && option.value === value)
    ? value
    : undefined;
}

/**
 * Ícone por tipo de produto. `product` é texto livre vindo da apólice (sem
 * enum no banco) — casa por palavra-chave, sem distinguir maiúsculas/acento.
 */
const normalizeForMatch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase();

export function ProductIcon({ product, ...props }) {
  const normalized = normalizeForMatch(product);
  if (normalized.includes("AUTO") || normalized.includes("MOTO")) {
    return <DirectionsCarIcon {...props} />;
  }
  if (
    normalized.includes("RESIDEN") ||
    normalized.includes("CONDOM") ||
    normalized.includes("IMOBILI")
  ) {
    return <HomeWorkIcon {...props} />;
  }
  if (normalized.includes("VIDA")) {
    return <FavoriteIcon {...props} />;
  }
  if (normalized.includes("EMPRESA") || normalized.includes("RESPONSABILIDADE")) {
    return <BusinessIcon {...props} />;
  }
  return <DescriptionIcon {...props} />;
}

/** "12345678900" -> "123.456.789-00"; "12345678000199" -> "12.345.678/0001-99". */
export function formatDocumento(documento) {
  const digits = String(documento || "").replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return documento || "—";
}

/** "5548999998888" -> "(48) 99999-8888". Mostra o número cru se não bater
 * com nenhum formato conhecido de telefone brasileiro. */
export function formatPhoneDisplay(number) {
  if (!number) return "—";
  let digits = String(number).replace(/\D/g, "");
  if (digits.length > 11 && digits.startsWith("55")) {
    digits = digits.slice(2);
  }
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
  }
  return number;
}

/* ------------------------------------------------------------------ */
/* Filtro "Mês de vencimento" (visão por apólice)                      */
/* ------------------------------------------------------------------ */

export const VENCIMENTO_TIMEZONE = "America/Sao_Paulo";
export const VENCIMENTO_MESES_PASSADOS = 18;
export const VENCIMENTO_MESES_FUTUROS = 11;

/** Ano/mês corrente em Brasília (não no fuso do navegador). `now` injetável. */
export function getSaoPauloYearMonth(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: VENCIMENTO_TIMEZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(now);
  const year = Number(parts.find((p) => p.type === "year").value);
  const month = Number(parts.find((p) => p.type === "month").value);
  return { year, month };
}

const pad2 = (n) => String(n).padStart(2, "0");

/** Soma `delta` meses a um YYYY-MM, só com aritmética inteira. */
export function addMonths(year, month, delta) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/**
 * Lista de YYYY-MM de (hoje - 18 meses) até (hoje + 11 meses), em ordem
 * cronológica (30 itens). Em 23/10/2026: 2025-04 a 2027-09.
 */
export function buildMonthOptions(now = new Date()) {
  const { year, month } = getSaoPauloYearMonth(now);
  const out = [];
  for (let delta = -VENCIMENTO_MESES_PASSADOS; delta <= VENCIMENTO_MESES_FUTUROS; delta += 1) {
    const m = addMonths(year, month, delta);
    out.push(`${m.year}-${pad2(m.month)}`);
  }
  return out;
}

/** "2026-10" -> "Out/2026". `t` é i18n.t (chaves carteiraVencimentos.months.m01..m12). */
export function formatMonthLabel(yearMonth, t) {
  const match = /^(\d{4})-(\d{2})$/.exec(String(yearMonth || ""));
  if (!match) return "";
  return `${t(`carteiraVencimentos.months.m${match[2]}`)}/${match[1]}`;
}

/** "2026-10-05" -> "05/10/2026", sem Date (evita deslocamento de fuso). */
export function formatDateBr(isoDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""));
  if (!match) return "—";
  return `${match[3]}/${match[2]}/${match[1]}`;
}

export const SELO_CONFIG = {
  Renovada: { color: "#2E7D32", labelKey: "renovada", tooltipKey: "tooltipRenovada" },
  Vencida: { color: "#B26A00", labelKey: "vencida", tooltipKey: "tooltipVencida" },
  "Não renovada": { color: "#C62828", labelKey: "naoRenovada", tooltipKey: "tooltipNaoRenovada" },
  Nova: { color: "#6A1B9A", labelKey: "nova", tooltipKey: "tooltipNova" },
};

/** Cor do chip de selo; null quando não há selo (chip em branco). */
export const seloColor = (selo) => (SELO_CONFIG[selo] ? SELO_CONFIG[selo].color : null);

/** Chave i18n (sob carteiraVencimentos.) do rótulo do selo; null sem selo. */
export const seloLabelKey = (selo) => (SELO_CONFIG[selo] ? SELO_CONFIG[selo].labelKey : null);

/** Chave i18n (sob carteiraVencimentos.) do tooltip do selo; null sem selo. */
export const seloTooltipKey = (selo) => (SELO_CONFIG[selo] ? SELO_CONFIG[selo].tooltipKey : null);
